import { create } from 'zustand';
import type { Channel } from '@shared/types.ts';
import { player } from '@/lib/player.ts';

/**
 * Recently watched, most recent first, persisted per device.
 *
 * The point is the two-channel case: you were on one thing, you flipped to
 * another, and you want the first one back without walking a grid of a
 * hundred. So the CURRENT channel is excluded from the list — the top entry
 * is always "the one before this one", which makes up-then-OK a reliable
 * two-press flip.
 *
 * localStorage rather than the server: this is a per-device convenience, not
 * an account fact, and the family's shared server has no business holding
 * "what the living-room TV watched at 9pm".
 */

const KEY = 'n8m.recents';
const MAX = 12;

function load(): Channel[] {
  try {
    const raw = localStorage.getItem(KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.slice(0, MAX) : [];
  } catch {
    // Private mode, cleared storage, a shape from an older build — none of
    // which is worth breaking the app over.
    return [];
  }
}

function save(list: Channel[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* not fatal */ }
}

interface RecentsState {
  channels: Channel[];
  push(channel: Channel): void;
}

export const useRecents = create<RecentsState>((set, get) => ({
  channels: load(),
  push: (channel) => {
    const next = [channel, ...get().channels.filter((c) => c.id !== channel.id)].slice(0, MAX);
    save(next);
    set({ channels: next });
  },
}));

/** Everything except what is playing right now. */
export function recentsExcluding(currentId: string | null): Channel[] {
  return useRecents.getState().channels.filter((c) => c.id !== currentId);
}

/**
 * Record every tune, from wherever it came — grid, channel-cycling, or the
 * recents strip itself. Watching the player rather than the call sites means
 * a future way to start playback cannot forget to log it.
 */
export function bindRecents(): () => void {
  let lastId: string | null = null;
  return player.subscribe((s) => {
    const id = s.channel?.id ?? null;
    if (id && id !== lastId) useRecents.getState().push(s.channel!);
    lastId = id;
  });
}
